/**
 * Copy as draft handler logic.
 * For Media Devoted | Created By Ciro Autiero
 */

import { toClickIdStructure, toDomainScheme } from '@trafficpulse/shared';

import {
  filterSenderNamesMax11,
  uniqueKeepOrderStrings,
} from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';
import type { ShortLinkTld } from '@/hooks';
import type { CreatePreparedDropPayload } from '@/hooks/smswiser-campaign-workspace/mutations';
import type { DropsWithStatsRow } from '@/features/smswiser-campaigns/types/workspace.types';
import { resolveLeadsListId, resolveRouteId, resolveTldValue, resolveWiserId } from '../resolvers';

export interface CopyAsDraftDeps {
  countryKey: string;
  campaigns: Array<{ id: number; name: string }>;
  primaryWiserId: number;
  leadsLists: { id: number; name: string }[];
  routes: { id: number; name: string }[];
  tlds: ShortLinkTld[];
  creativeSmsTexts: string[] | undefined;
  updateCreative: { mutateAsync: (data: { smsTexts: string[] }) => Promise<unknown> };
  createPreparedFlex: {
    mutateAsync: (opts: {
      countryKey: string;
      payload: CreatePreparedDropPayload;
      wiserId: number;
    }) => Promise<unknown>;
  };
  toastError: (title: string, msg: string) => void;
}

export async function executeCopyAsDraft(
  rows: DropsWithStatsRow[],
  targetWorkDate: string,
  deps: CopyAsDraftDeps,
  gatewayOverrides?: Record<string, number>,
  staticTagId?: string | null,
): Promise<number> {
  const allTextsFromRows = rows.flatMap((r) =>
    Array.isArray(r.drop.smsText)
      ? r.drop.smsText.filter((t): t is string => typeof t === 'string')
      : r.drop.smsText
        ? [String(r.drop.smsText)]
        : [],
  );
  const mergedForDb = uniqueKeepOrderStrings([...(deps.creativeSmsTexts ?? []), ...allTextsFromRows])
    .filter((t) => String(t).trim().length > 0)
    .slice(0, 50);
  if (mergedForDb.length > 0) {
    await deps.updateCreative.mutateAsync({ smsTexts: mergedForDb });
  }

  let created = 0;
  for (const row of rows) {
    const wiserId =
      deps.campaigns.length > 0 ? resolveWiserId(row.drop.campaign, deps.campaigns) : deps.primaryWiserId;
    if (wiserId == null || wiserId <= 0) {
      deps.toastError('Copy as draft failed', `Could not resolve campaign for drop "${row.drop.title}" (campaign: ${row.drop.campaign ?? '—'}).`);
      continue;
    }
    const dropKey = row.drop.uniqueId ?? String(row.drop.id);
    const routeId = resolveRouteId(row.drop.smsGatewayRoute, deps.routes) ?? gatewayOverrides?.[dropKey] ?? null;
    if (routeId == null) {
      deps.toastError('Copy as draft failed', `No gateway for drop: ${row.drop.title}. Select gateway in the modal.`);
      continue;
    }
    const leadsListWiserId = resolveLeadsListId(row.drop.leadsList, deps.leadsLists);
    if (leadsListWiserId == null) {
      deps.toastError('Copy as draft failed', `Could not resolve leads list for drop: ${row.drop.title}`);
      continue;
    }
    const shortLinkTldValue = resolveTldValue(row.drop.shortLinkTld, deps.tlds);
    if (!shortLinkTldValue) {
      deps.toastError('Copy as draft failed', 'No short link TLD available.');
      continue;
    }
    const rawSenderNames = Array.isArray(row.drop.senderName)
      ? row.drop.senderName.filter((s): s is string => typeof s === 'string').slice(0, 50)
      : row.drop.senderName
        ? [String(row.drop.senderName)]
        : [];
    const senderNames = filterSenderNamesMax11(rawSenderNames);
    const smsTexts = Array.isArray(row.drop.smsText)
      ? row.drop.smsText.filter((t): t is string => typeof t === 'string').slice(0, 50)
      : row.drop.smsText
        ? [String(row.drop.smsText)]
        : [];
    if (senderNames.length === 0 || smsTexts.length === 0) {
      deps.toastError('Copy as draft failed', `Drop "${row.drop.title}" has no sender or SMS text.`);
      continue;
    }
    const rowStaticTagId = row.drop.staticTag?.id ?? staticTagId;
    const createPayload: CreatePreparedDropPayload = {
      workDate: targetWorkDate,
      routeId,
      leadsListWiserId,
      shortLinkTldValue,
      amount: row.drop.cap ?? 500,
      senderNames,
      smsTexts,
      clickIdStructure: toClickIdStructure(row.drop.clickIdStructure),
      domainScheme: toDomainScheme(row.drop.domainScheme),
      ...(rowStaticTagId != null && { staticTagId: rowStaticTagId }),
    };
    await deps.createPreparedFlex.mutateAsync({
      countryKey: deps.countryKey,
      payload: createPayload,
      wiserId,
    });
    created++;
  }
  return created;
}
